import React, {useMemo} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import {
    buildCollectionEeiSegments,
    collectionCountsText,
    resolveIcrCollectionHeadlinePct,
} from '../buildCollectionCoverageView';
import CoverageMetric from '../components/CoverageMetric';
import CoverageScoreRing from '../components/CoverageScoreRing';
import CollectionCoverageEeiBar from './CollectionCoverageEeiBar';

/**
 * Collection Coverage overview: ICR headline % ring (same value as Coverage tab),
 * per-EEI bar, and material/citation count metrics.
 */
export default function CollectionCoverageOverviewTab({
    view,
    pirEeis = null,
    computing = false,
}) {
    const eeiSegments = useMemo(
        () => (view ? buildCollectionEeiSegments(view, pirEeis) : []),
        [view, pirEeis],
    );
    // Same headline as Coverage tab Total card / modal ring (never EEI segment average).
    const collectionScore = useMemo(
        () => resolveIcrCollectionHeadlinePct(view),
        [view],
    );

    const linkedCount = view?.linked?.materialCount ?? view?.linked?.materials?.length ?? 0;
    const suggestedCount = view?.discoveredCount
        ?? view?.unlinked?.materialCount
        ?? view?.unlinked?.materials?.length
        ?? 0;
    const citationCount = view?.fintelUsage?.citedCount ?? 0;
    const countsText = collectionCountsText(eeiSegments);

    return (
        <Stack spacing={3}>
            <Stack direction="row" spacing={2.5} alignItems="flex-start">
                <CoverageScoreRing
                    score={computing ? null : collectionScore}
                    computing={computing}
                    label={!computing && collectionScore == null ? '—' : undefined}
                    size={88}
                    strokeWidth={4}
                />
                <Box sx={{flex: 1, minWidth: 0}}>
                    {computing && eeiSegments.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.5}}>
                            Computing collection coverage…
                        </Typography>
                    ) : !view ? (
                        <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.5}}>
                            Collection coverage has not been computed for this ICR yet.
                        </Typography>
                    ) : eeiSegments.length > 0 ? (
                        <>
                            {countsText && (
                                <Typography variant="body2" fontWeight={600} sx={{mb: 1.5, lineHeight: 1.5}}>
                                    {countsText}
                                </Typography>
                            )}
                            <CollectionCoverageEeiBar
                                segments={eeiSegments}
                                fullWidth
                            />
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.5}}>
                            No EEI coverage breakdown available yet.
                        </Typography>
                    )}
                </Box>
            </Stack>

            {!computing && view && (
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                    <CoverageMetric label="Linked materials" value={linkedCount}/>
                    <CoverageMetric label="Materials suggested by Minsky" value={suggestedCount}/>
                    <CoverageMetric label="Citations" value={citationCount}/>
                </Stack>
            )}
        </Stack>
    );
}
